// src/hooks/useSplitTask.ts
import { useState } from 'react';
import { Task } from '../types/task';
import { taskService } from '../features/task';
import { getCurrentUserId } from '../services/auth';
import { logError } from '../utils/logger';

export function useSplitTask() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [subtasks, setSubtasks] = useState<Partial<Task>[]>([]);
  
  /**
   * 使用 AI 分割任務
   */
  const splitTask = async (
    task: Task, 
    options?: { 
      generationNotes?: string,
      onSuccess?: (subtasks: Partial<Task>[]) => void,
      onError?: (error: string) => void
    }
  ) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userId = getCurrentUserId();
      const currentDate = new Date().toISOString().split('T')[0];
      
      // 調用 API 取得 AI 分割建議
      const result = await taskService.splitTaskWithAI(task, userId, {
        currentDate,
        context: {
          generationNotes: options?.generationNotes
        }
      });
      
      if (result.subtasks && Array.isArray(result.subtasks)) {
        setSubtasks(result.subtasks);
        if (options?.onSuccess) {
          options.onSuccess(result.subtasks);
        }
        return result.subtasks;
      } else {
        throw new Error('無效的子任務格式');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知錯誤';
      logError('任務分割失敗', error);
      setError(errorMessage);
      if (options?.onError) {
        options.onError(errorMessage);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * 保存分割後的子任務
   */
  const saveSplitTasks = async (
    parentTaskId: string,
    subtasksToSave: Partial<Task>[],
    onComplete?: () => void
  ) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userId = getCurrentUserId();
      await taskService.splitTask(parentTaskId, subtasksToSave, userId);
      if (onComplete) onComplete();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知錯誤';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    isLoading,
    error,
    subtasks,
    splitTask,
    saveSplitTasks
  };
}